/**
 * GraphQL contract for the Advanced Analytics resolver.
 *
 * The front sends a semantic `AnalyticsQueryInput`; the resolver translates it
 * to parameterized ClickHouse SQL (anti-injection, caching, permissions).
 *
 * TODO: backend not implemented yet. This document is the agreed contract —
 * wire `useAnalyticsQuery` to it once `runAnalyticsQuery` exists on the API.
 */
import { gql } from "@apollo/client";

export const RUN_ANALYTICS_QUERY = gql`
  query RunAnalyticsQuery($input: AnalyticsQueryInput!) {
    runAnalyticsQuery(input: $input) {
      series {
        date
        value
        breakdown
      }
      total
      comparison {
        total
        percentChange
      }
    }
  }
`;


export const SEARCH_METRICS_QUERY = gql`
  query SearchMetrics($searchType: String!, $startDate: DateTime!, $endDate: DateTime!) { 
      searchMetrics(searchType: $searchType, startDate: $startDate, endDate: $endDate) {
        current {
          totalSearches
          uniqueUsers
          uniqueDestinations
          searchesPerUser
          resultsPerSearch
          noResultsRate
          avgResponseTime
          avgNights
          avgRooms
          avgGuests
          avgAdvanceDays
        }
        previous {
          totalSearches
          uniqueUsers
          uniqueDestinations
          searchesPerUser
          resultsPerSearch
          noResultsRate
          avgResponseTime
          avgNights
          avgRooms
          avgGuests
          avgAdvanceDays
        }
      }
  }
`;

export const FLIGHTS_SEARCH_QUERY = gql`
  query FlightSearch($startDate: DateTime!, $endDate: DateTime!) { 
      flightSearch(startDate: $startDate, endDate: $endDate) {
        searchesTrend {
          timestamp
          value
        }
        searchesByTripType {
          key
          count
          percentage
        }
        searchesByClass {
          key
          count
          percentage
        }
        leadTimeDistribution {
          key
          count
          percentage
        }
        pricesTrend {
          timestamp
          value
        }
      }
  }
`;

export const HOTELS_SEARCH_QUERY = gql`
  query HotelSearch($startDate: DateTime!, $endDate: DateTime!, $topDestinationsPageSize: Int) {
      hotelSearch(startDate: $startDate, endDate: $endDate, topDestinationsPageSize: $topDestinationsPageSize) {
        searchesTrend {
          timestamp
          value
        }
        leadTimeDistribution {
          key
          count
          percentage
        }
        stayLengthDistribution {
          key
          count
          percentage
        }
        roomsDistribution {
          key
          count
          percentage
        }
        guestsDistribution {
          key
          count
          percentage
        }
        topDestinations {
          id
          destinationId
          destinationName
          searches
          avgNights
          avgGuests
        }
      }
  }
`;

export const CONVERSION_FUNNEL_QUERY = gql`
  query ConversionFunnel($startDate: DateTime!, $endDate: DateTime!) {
      conversionFunnel(startDate: $startDate, endDate: $endDate) {
        steps {
          id
          step
          count
          percentage
          dropoffRate
        }
        overallConversionCurrent
        overallConversionPrevious
        conversionTrend {
          id
          timestamp
          value
        }
        funnelByProduct {
          id
          product
          conversionRate
          steps {
            id
            step
            count
            percentage
            dropoffRate
          }
        }
      }
  }
`;

export const SEARCH_EXPLORER_QUERY = gql`
  query SearchExplorer($startDate: DateTime!, $endDate: DateTime!, $pageNumber: Int!, $pageSize: Int!, $skipTypes: [String!], $sortBy: String, $sortOrder: String) {
      searchExplorer(startDate: $startDate, endDate: $endDate, pageNumber: $pageNumber, pageSize: $pageSize, skipTypes: $skipTypes, sortBy: $sortBy, sortOrder: $sortOrder) {
        count
        pageNumber
        pageSize
        hasNextPage
        hasPreviousPage
        items {
          id
          searchType
          createdAt
          origin
          originName
          destination
          destinationName
          destinationId
          cityId
          cityName
          cityKey
          departureDate
          returnDate
          numNights
          numDays
          checkInDate
          pickupDate
          dropoffDate
          pickupLocationCode
          dropoffLocationCode
          hotelSearchType
          numRooms
          flightClass
          flightSearchType
          luggageType
          serviceLanguage
          numAdults
          numChildren
          resultsCount
          responseTimeMs
          converted
          segments {
            id
            productType
            origin
            originName
            destination
            destinationId
            destinationName
            cityId
            cityName
            startDate
            endDate
          }
        }
      }
  }
`;

export const OVERVIEW_ANALYTICS_QUERY = gql`
  query OverviewAnalytics($startDate: DateTime!, $endDate: DateTime!, $topRoutesLimit: Int) {
    overviewAnalytics(startDate: $startDate, endDate: $endDate, topRoutesLimit: $topRoutesLimit) {
      summary {
        totalSearches
        previousTotalSearches
        uniqueUsers
        previousUniqueUsers
        totalResults
        previousTotalResults
        avgResultsPerSearch
        previousAvgResultsPerSearch
        noResultRate
        previousNoResultRate
        searchToBookingRate
        previousSearchToBookingRate
        avgResponseTime
        previousAvgResponseTime
        errorRate
        previousErrorRate
      }
      searchesTrend {
        id
        timestamp
        value
      }
      searchesByProduct {
        id
        key
        count
        percentage
      }
      topOrigins {
        id
        code
        name
        searches
        percentage
      }
      topDestinations {
        id
        code
        name
        searches
        percentage
      }
    }
  }
`;

export const PERFORMANCE_ANALYTICS_QUERY = gql`
  query PerformanceAnalytics($startDate: DateTime!, $endDate: DateTime!) {
    performanceAnalytics(startDate: $startDate, endDate: $endDate) {
      summary {
        avgResponseTime
        previousAvgResponseTime
        p50ResponseTime
        previousP50ResponseTime
        p95ResponseTime
        previousP95ResponseTime
        p99ResponseTime
        previousP99ResponseTime
        errorRate
        previousErrorRate
        timeoutRate
        previousTimeoutRate
        cacheHitRate
        previousCacheHitRate
      }
      responseTimeDistribution {
        id
        key
        count
        percentage
      }
      responseTimeTrend {
        id
        timestamp
        value
      }
      errorsTrend {
        id
        timestamp
        value
      }
      performanceByProduct {
        id
        product
        searchVolume
        avgResponseTime
        p95ResponseTime
        errorRate
        timeoutRate
      }
      slowestRoutes {
        id
        origin
        destination
        avgResponseTime
        searchCount
      }
    }
  }
`;

/**
 * Server-side SDL reference (for the backend team):
 *
 * enum ReportType { INSIGHTS FUNNEL FLOW RETENTION }
 * input MetricInput { event: String! aggregation: Aggregation! property: String }
 * input StepInput { event: String! label: String }
 * input FilterInput { property: String! operator: String! value: JSON }
 * input BreakdownInput { property: String! }
 * input DateRangeInput { from: DateTime! to: DateTime! granularity: Granularity }
 * input AnalyticsQueryInput {
 *   reportType: ReportType!
 *   metrics: [MetricInput!]
 *   steps: [StepInput!]
 *   filters: [FilterInput!]
 *   breakdown: BreakdownInput
 *   dateRange: DateRangeInput!
 *   compareTo: DateRangeInput
 *   cohortId: ID
 *   windowSeconds: Int
 * }
 */
